/* "A new version is available".
 *
 * A dialog, where `pwa/UpdatePrompt` is a bar — and the difference is the
 * point rather than an inconsistency.
 *
 * The bar is right for what it reports: a newer service worker has installed
 * and is waiting, the running app is fine, and the swap can happen whenever.
 * Interrupting somebody to tell them that is how people learn to avoid
 * installing web apps.
 *
 * This one reports something else. The build in front of the user is not the
 * build on the server, and every route they open from here is the old one.
 * That is not a background nicety, it is the app quietly being wrong, and it
 * is the thing people have been fixing by clearing their browser cache. It
 * gets a dialog.
 *
 * It is still dismissible. A modal you cannot leave is a trap, and somebody
 * halfway through a basket should be able to finish and reload afterwards —
 * the tab is stale either way, and taking their session away to fix it is a
 * worse trade than letting them choose the moment. */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '../ui/Modal'
import { applyNewVersion, onNewVersion, startVersionWatch } from '../../lib/version'
import { onUpdateAvailable } from '../../pwa/register'

export function UpdateDialog() {
  const { t } = useTranslation()
  const [stale, setStale] = useState(false)
  const [workerHasIt, setWorkerHasIt] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [busy, setBusy] = useState(false)

  /* The watch lives with the thing that reacts to it, rather than in main.tsx,
     so there is one place to read to know both when the question is asked and
     what happens to the answer. */
  useEffect(() => startVersionWatch(), [])
  useEffect(() => onNewVersion(() => setStale(true)), [])

  /* Both signals mean "there is a newer build", so only one of them should be
     on screen. The bar wins when it is up: it is already offering the cheaper
     fix — the waiting worker activates and the page reloads onto it, with the
     precache intact — and it can act without dropping anything. This dialog is
     for the far commoner case where nothing noticed at all.
     A bar the user dismissed keeps this suppressed, which is correct: they were
     told and they declined. */
  useEffect(() => onUpdateAvailable(setWorkerHasIt), [])

  const open = stale && !workerHasIt && !dismissed

  return (
    <Modal
      open={open}
      onClose={() => setDismissed(true)}
      title={t('pwa.staleTitle')}
      className="modal-confirm"
      footer={
        <button
          type="button"
          className="btn pri full"
          disabled={busy}
          onClick={() => {
            /* Latched rather than reset on failure: `applyNewVersion` ends in a
               reload, so there is no "after" in which to re-enable this, and a
               second press while the caches are being dropped would race the
               first. */
            setBusy(true)
            void applyNewVersion()
          }}
        >
          {busy ? t('common.loading') : t('pwa.staleAction')}
        </button>
      }
    >
      <p className="modal-text">{t('pwa.staleBody')}</p>
    </Modal>
  )
}
